import { formatMoney, nameCell } from '@/components/data-table/columns-helper';
import { signedMoney } from '@/pages/settlements/signed-money';
import { cn } from '@/lib/utils';
import { MemberNameCell } from '@/components/MemberDrawer';

// ---------------------------------------------------------------------------
// Shared dashboard widgets — consumed by both the reports OverviewPage and the
// home DashboardPage so the two stay visually + behaviourally identical (DRY).
//
// Money is integer minor units serialized as a JS number (number-mode). Format
// table figures via formatMoney({ currency }) and signed P&L via signedMoney;
// never `Number(x) / 100` (corrupts non-2dp currencies — JPY/KRW 0dp, Gulf 3dp).
// ---------------------------------------------------------------------------

export type TFn = (key: string) => string;

/**
 * One calendar day of settled cash, for ONE denomination. The row carried no
 * currency at all until #532, so a scope spanning two brands added their minor
 * units before any screen could tell them apart.
 */
export interface FinanceDailyRow {
  day: string;
  currency: string;
  deposit: number;
  withdraw: number;
  netFlow: number;
  depositTxCount: number;
  withdrawTxCount: number;
}

export interface WinlossDailyRow {
  day: string;
  providerId: string | null;
  gameType: string | null;
  currency: string;
  turnover: number;
  payout: number;
  winloss: number;
  betCount: number;
  playerCount: number;
}

export interface MemberReportRow {
  memberId: string;
  username: string;
  currency: string;
  deposit: number;
  withdraw: number;
  turnover: number;
  winloss: number;
  /** `null` = no data source yet, never zero. */
  bonusOut: number | null;
  lastBetAt: string | null;
}

// ---------------------------------------------------------------------------
// Aggregation helpers (pure, currency-aware — never mix currencies)
// ---------------------------------------------------------------------------

export interface ProviderRollup {
  providerId: string;
  turnover: number;
  payout: number;
  winloss: number;
  betCount: number;
}

/** The bucket a rollup row with no provider id falls into. Not a provider id:
 * nothing can be drilled or filtered by it, so screens must not offer to. */
export const UNMAPPED_PROVIDER_ID = '—';

/**
 * Roll the per-day×provider×gameType×currency win/loss rows up to one row per
 * provider, scoped to a single currency. Money stays in integer minor units
 * (JS number-mode, safe-integer range for any realistic period); never sum
 * across currencies — the caller picks one currency block first.
 */
export function rollupByProvider(
  rows: WinlossDailyRow[],
  currency: string | null,
): ProviderRollup[] {
  const by = new Map<string, ProviderRollup>();
  for (const r of rows) {
    if (r.currency !== currency) continue;
    const id = r.providerId ?? UNMAPPED_PROVIDER_ID;
    let acc = by.get(id);
    if (!acc) {
      acc = { providerId: id, turnover: 0, payout: 0, winloss: 0, betCount: 0 };
      by.set(id, acc);
    }
    acc.turnover += r.turnover;
    acc.payout += r.payout;
    acc.winloss += r.winloss;
    acc.betCount += r.betCount;
  }
  return [...by.values()].sort((a, b) => b.turnover - a.turnover);
}

/**
 * Per-day win/loss totals for a single currency (for the trend chart and the
 * daily table).
 *
 * `playerCount` is deliberately absent. The source rows are grouped by
 * day x provider x gameType, and each carries a DISTINCT member count for its
 * own group, so adding them up double-counts anyone who played two providers on
 * the same day. A per-day distinct count has to come from the service.
 */
export interface WinlossDayPoint {
  day: string;
  turnover: number;
  payout: number;
  winloss: number;
  betCount: number;
}

export function rollupByDay(
  rows: WinlossDailyRow[],
  currency: string | null,
): WinlossDayPoint[] {
  const by = new Map<string, WinlossDayPoint>();
  for (const r of rows) {
    if (r.currency !== currency) continue;
    let acc = by.get(r.day);
    if (!acc) {
      acc = { day: r.day, turnover: 0, payout: 0, winloss: 0, betCount: 0 };
      by.set(r.day, acc);
    }
    acc.turnover += r.turnover;
    acc.payout += r.payout;
    acc.winloss += r.winloss;
    acc.betCount += r.betCount;
  }
  return [...by.values()].sort((a, b) => a.day.localeCompare(b.day));
}

/**
 * The currency carrying the most turnover across the win/loss rows, or `null`
 * when there are no rows to derive one from.
 *
 * It used to answer the literal `'THB'` for an empty list, and the callers feed
 * that to money tiles fed by a DIFFERENT query — so a JPY brand's deposits
 * rendered with a baht glyph and a divide-by-100 while the win/loss query was
 * still in flight (the normal render order), and permanently for a brand with
 * no settled bets in the window or if that query failed. That is the same
 * "THB restored behind a race instead of a constant" the money-column fix
 * exists to remove: unknown renders as unknown, via `formatMoneyIn` (W3).
 */
export function dominantCurrency(rows: { currency: string; turnover: number }[]): string | null {
  const by = new Map<string, number>();
  for (const r of rows) by.set(r.currency, (by.get(r.currency) ?? 0) + r.turnover);
  let best: string | null = null;
  let bestVol = -1;
  for (const [currency, vol] of by) {
    if (vol > bestVol) {
      bestVol = vol;
      best = currency;
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Layout primitives
// ---------------------------------------------------------------------------

export function ChartSkeleton() {
  return <div className="h-32 animate-pulse rounded bg-muted" />;
}
export function ListSkeleton() {
  return <div className="h-28 animate-pulse rounded bg-muted" />;
}
export function EmptyHint({ label }: { label: string }) {
  return (
    <div className="flex h-28 items-center justify-center text-sm text-muted-foreground">
      {label}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Provider / member lists
// ---------------------------------------------------------------------------

export function ProviderList({ rows, currency, names }: { rows: ProviderRollup[]; currency: string | null; names: Map<string, string> }) {
  if (!currency) return <EmptyHint label="—" />;
  return (
    <ul className="divide-y">
      {rows.map((r, i) => {
        const wl = signedMoney(r.winloss, currency);
        return (
          <li
            key={r.providerId}
            className="flex items-center justify-between gap-2 py-2 text-sm"
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded bg-muted text-xs font-semibold text-muted-foreground">
                {i + 1}
              </span>
              <span className="truncate font-medium">{nameCell(names.get(r.providerId), r.providerId)}</span>
            </span>
            <span className="flex shrink-0 items-center gap-3 tabular-nums">
              <span className="text-muted-foreground">{formatMoney(r.turnover, { currency })}</span>
              <span
                className={cn(
                  'w-24 text-right',
                  wl.negative && 'text-destructive',
                  !wl.negative && !wl.zero && 'text-emerald-600 dark:text-emerald-400',
                )}
              >
                {wl.text}
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Top-member list. The `metric` prop picks which figure leads the row:
 * 'turnover' (biggest players) or 'winloss' (biggest winners/losers). Both
 * always show the player USERNAME (never the raw memberId UUID) — the report
 * endpoint carries `username`, so risk operators read names, not opaque ids.
 */
export function TopMemberList({
  rows,
  t,
  metric = 'both',
}: {
  rows: MemberReportRow[];
  t: TFn;
  metric?: 'both' | 'turnover' | 'winloss';
}) {
  return (
    <ul className="divide-y">
      {metric === 'both' ? (
        <li className="flex items-center justify-between gap-2 pb-1 text-xs text-muted-foreground">
          <span>{t('reports.dashboardTab.member')}</span>
          <span className="flex shrink-0 items-center gap-3">
            <span className="w-28 text-right">{t('reports.turnover')}</span>
            <span className="w-24 text-right">{t('reports.winloss')}</span>
          </span>
        </li>
      ) : null}
      {rows.map((m, i) => {
        const wl = signedMoney(m.winloss, m.currency);
        return (
          <li
            key={`${m.memberId}|${m.currency}`}
            className="flex items-center justify-between gap-2 py-2 text-sm"
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded bg-muted text-xs font-semibold text-muted-foreground">
                {i + 1}
              </span>
              <span className="truncate font-medium">
                <MemberNameCell name={m.username} id={m.memberId} />
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-3 tabular-nums">
              {metric !== 'winloss' ? (
                <span className="w-28 text-right text-muted-foreground">
                  {formatMoney(m.turnover, { currency: m.currency })}
                </span>
              ) : null}
              {metric !== 'turnover' ? (
                <span
                  className={cn(
                    'w-24 text-right',
                    wl.negative && 'text-destructive',
                    !wl.negative && !wl.zero && 'text-emerald-600 dark:text-emerald-400',
                  )}
                >
                  {wl.text}
                </span>
              ) : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// SVG charts — dependency-free. Compact: h-32.
// ---------------------------------------------------------------------------

/**
 * Per-day cash totals for a SINGLE currency.
 *
 * The feed is one row per (currency, day), and bars drawn from two
 * denominations on one axis are not comparable heights — ¥1,500 would tower
 * over ฿15.00 (#532). The caller picks a denomination first.
 */
export function rollupFinanceByDay(
  rows: FinanceDailyRow[],
  currency: string | null,
): FinanceDailyRow[] {
  const by = new Map<string, FinanceDailyRow>();
  for (const r of rows) {
    if (r.currency !== currency) continue;
    let acc = by.get(r.day);
    if (!acc) {
      acc = { ...r, deposit: 0, withdraw: 0, netFlow: 0, depositTxCount: 0, withdrawTxCount: 0 };
      by.set(r.day, acc);
    }
    acc.deposit += r.deposit;
    acc.withdraw += r.withdraw;
    acc.netFlow += r.netFlow;
    acc.depositTxCount += r.depositTxCount;
    acc.withdrawTxCount += r.withdrawTxCount;
  }
  return [...by.values()].sort((a, b) => a.day.localeCompare(b.day));
}

/**
 * The currency carrying the most settled cash across the daily rows, or `null`
 * when there is nothing to derive one from. Same rule as `dominantCurrency`,
 * over volume rather than turnover: unknown renders as unknown.
 */
export function dominantCashCurrency(rows: FinanceDailyRow[]): string | null {
  return dominantCurrency(rows.map((r) => ({ currency: r.currency, turnover: r.deposit + r.withdraw })));
}

/** Grouped deposit/withdraw bars per day — deposit (emerald) + withdraw (amber). */
export function FinanceTrendChart({ rows, t }: { rows: FinanceDailyRow[]; t: TFn }) {
  if (rows.length === 0) return <EmptyHint label="—" />;
  const data = [...rows].sort((a, b) => a.day.localeCompare(b.day));
  const maxRaw = Math.max(1, ...data.map((d) => Math.max(d.deposit, d.withdraw)));
  const W = 720;
  const H = 200;
  const padX = 32;
  const padY = 24;
  const innerW = W - padX * 2;
  const innerH = H - padY * 2;
  const groupW = innerW / data.length;
  const barW = Math.max(3, groupW / 3);
  return (
    <div className="w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-32 w-full min-w-[480px]"
        preserveAspectRatio="none"
        role="img"
        aria-label={t('reports.dashboardTab.dailyMoneyTrend')}
      >
        {[0.25, 0.5, 0.75, 1].map((frac) => (
          <line
            key={frac}
            x1={padX}
            x2={W - padX}
            y1={padY + innerH * (1 - frac)}
            y2={padY + innerH * (1 - frac)}
            className="stroke-border"
            strokeDasharray="2 4"
          />
        ))}
        {data.map((d, i) => {
          const cx = padX + groupW * i + groupW / 2;
          const depH = (d.deposit / maxRaw) * innerH;
          const wdH = (d.withdraw / maxRaw) * innerH;
          return (
            <g key={d.day}>
              <rect
                x={cx - barW - 1}
                y={padY + innerH - depH}
                width={barW}
                height={depH}
                className="fill-emerald-500/70"
                rx={1}
              />
              <rect
                x={cx + 1}
                y={padY + innerH - wdH}
                width={barW}
                height={wdH}
                className="fill-amber-500/70"
                rx={1}
              />
              <text
                x={cx}
                y={H - 6}
                textAnchor="middle"
                className="fill-muted-foreground text-[10px]"
              >
                {d.day.slice(5)}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="mt-1 flex items-center justify-end gap-4 text-xs text-muted-foreground">
        <LegendDot className="bg-emerald-500/70" label={t('finance.deposits')} />
        <LegendDot className="bg-amber-500/70" label={t('finance.withdraws')} />
      </div>
    </div>
  );
}

/** Daily win/loss line — a single polyline with a zero baseline. Win/loss can
 * be negative (operator paid out), so the y-axis is centred on zero. */
export function WinlossTrendChart({
  points,
  currency,
  t,
}: {
  points: WinlossDayPoint[];
  currency: string | null;
  t: TFn;
}) {
  if (points.length === 0) return <EmptyHint label="—" />;
  const W = 360;
  const H = 200;
  const padX = 28;
  const padY = 20;
  const innerW = W - padX * 2;
  const innerH = H - padY * 2;
  const vals = points.map((p) => p.winloss);
  const maxAbs = Math.max(1, ...vals.map((v) => Math.abs(v)));
  // y(value): 0 maps to the vertical centre; +maxAbs to top, −maxAbs to bottom.
  const y = (v: number) => padY + innerH / 2 - (v / maxAbs) * (innerH / 2);
  const x = (i: number) =>
    padX + (points.length === 1 ? innerW / 2 : (innerW * i) / (points.length - 1));
  const linePts = points.map((p, i) => `${x(i)},${y(p.winloss)}`).join(' ');
  const zeroY = y(0);
  return (
    <div className="w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-32 w-full min-w-[280px]"
        preserveAspectRatio="none"
        role="img"
        aria-label={t('reports.dashboardTab.dailyWinlossTrend')}
      >
        <line
          x1={padX}
          x2={W - padX}
          y1={zeroY}
          y2={zeroY}
          className="stroke-border"
          strokeDasharray="2 4"
        />
        <polyline
          points={linePts}
          fill="none"
          className="stroke-sky-500"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {points.map((p, i) => (
          <g key={p.day}>
            <circle
              cx={x(i)}
              cy={y(p.winloss)}
              r={2.5}
              className={cn(
                p.winloss < 0 ? 'fill-destructive' : 'fill-emerald-500',
              )}
            />
            <text
              x={x(i)}
              y={H - 4}
              textAnchor="middle"
              className="fill-muted-foreground text-[10px]"
            >
              {p.day.slice(5)}
            </text>
          </g>
        ))}
      </svg>
      <div className="mt-1 flex items-center justify-end gap-4 text-xs text-muted-foreground">
        <span>{t('reports.winloss')}</span>
        <span className="tabular-nums">{currency ?? '-'}</span>
      </div>
    </div>
  );
}

export function LegendDot({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={cn('inline-block h-2 w-2 rounded-sm', className)} />
      {label}
    </span>
  );
}
